import React, { type FC } from 'react'
import type {
  ColorType,
  ComponentThemeFactory,
  ComponentThemeProps,
  ConsumerRTHData,
  ConsumerRTHSeries,
} from '../../../themes/util/types'
import { useComponentStyle, useTheme } from '../../../themes'

import type { ConsumerRTHTableTheme } from '.'
import type { CSSProperties } from 'styled-components'

declare module '../../../themes/util/types' {
  interface ComponentThemes {
    ConsumerRTHTable: ConsumerRTHTableTheme
  }
}

export type ConsumerRTHTableProps =
  ComponentThemeProps<ConsumerRTHTableTheme> & {
    rthData?: ConsumerRTHData
  }

const getStyleByIndex = (index: number, colors: ColorType) => {
  if (index % 2 === 0) {
    return {
      style: {
        backgroundColor: String(colors.consumerRTH.divider),
      },
    }
  }
  return {}
}

const baseConsumerRTHTableTheme: ComponentThemeFactory<
  ConsumerRTHTableTheme
> = ({ colors, fonts }) => ({
  Table: [
    {
      style: {
        border: `1px solid ${String(colors.consumerRTH.border)}`,
        borderCollapse: 'collapse',
      },
    },
  ],
  FirstTableHeaderCell: [
    {
      style: {
        border: `1px solid ${String(colors.consumerRTH.border)}`,
        borderCollapse: 'collapse',
        minWidth: '200px',
      },
    },
  ],
  TableHeaderCell: [
    {
      style: {
        border: `1px solid ${String(colors.consumerRTH.border)}`,
        borderCollapse: 'collapse',
        justifyContent: 'center',
        minWidth: '60px',
        fontFamily: String(fonts.defaultSemibold),
        fontSize: '14px',
        color: String(colors.consumerRTH.tableHeaderText),
      },
    },
  ],
  FirstColumn: [
    {
      style: {
        border: `1px solid ${String(colors.consumerRTH.border)}`,
        borderCollapse: 'collapse',
        minWidth: '200px',
        padding: '8px',
        textAlign: 'center',
        fontFamily: String(fonts.defaultLight),
        fontSize: '14px',
      },
    },
  ],
  TableRow: [
    {
      style: {
        textAlign: 'center',
        fontFamily: String(fonts.defaultLight),
        fontSize: '14px',
        color: String(colors.consumerRTH.tableText),
      },
    },
  ],
  TableCell: [
    {
      style: {
        border: `1px solid ${String(colors.consumerRTH.border)}`,
        borderCollapse: 'collapse',
        padding: '8px',
      },
    },
  ],
})

const defaultRthData = {
  categories: [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
    'Monday',
    'Tuesday',
  ],
  series: [
    {
      name: 'Outdoor High Temp. (°F)',
      data: [56.0, 29.0, 43.0, 53.0, 66.0, 54.0, 59.0, 57.0, 49.0],
      stack: null,
      state: 'outdoor_high_temperature',
    },
  ],
}

const ConsumerRTHTable: FC<ConsumerRTHTableProps> = ({
  styles,
  decoration,
  layout,
  rthData = defaultRthData,
}) => {
  const consumerRTHTableStyles = useComponentStyle(
    'ConsumerRTHTable',
    baseConsumerRTHTableTheme,
    styles,
    layout,
    decoration,
  )
  const { colors } = useTheme()

  const days = {
    Sunday: 'Sun',
    Monday: 'Mon',
    Tuesday: 'Tue',
    Wednesday: 'Wed',
    Thursday: 'Thu',
    Friday: 'Fri',
    Saturday: 'Sat',
  }

  const getShortDays = (rthData.categories as Array<keyof typeof days>).map(
    function (day) {
      return days[day]
    },
  )

  const categories =
    rthData.categories.includes('Monday') ? getShortDays : rthData.categories
  const filterData = (rthData.series as ConsumerRTHSeries[]).filter(
    (series) =>
      !series.state.includes('heat') &&
      !series.state.includes('cool') &&
      series.data.some((data) => data !== null),
  )

  return (
    <>
      {rthData.categories.length !== 0 && (
        <table style={consumerRTHTableStyles.Table as CSSProperties}>
          <thead>
            <tr style={consumerRTHTableStyles.TableRow as CSSProperties}>
              <td
                style={
                  consumerRTHTableStyles.FirstTableHeaderCell as CSSProperties
                }
              />
              {categories.map((category, index) => (
                <td
                  style={{
                    ...(consumerRTHTableStyles.TableHeaderCell as CSSProperties),
                    ...getStyleByIndex(index, colors).style,
                  }}
                  key={category + index.toString()}
                >
                  {category}
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {filterData.map((series, index) => (
              <tr
                style={consumerRTHTableStyles.TableRow as CSSProperties}
                key={rthData.categories[index] + series.name}
              >
                <td style={consumerRTHTableStyles.FirstColumn as CSSProperties}>
                  {series.name}
                </td>
                {series.data.map((data, index) => (
                  <td
                    style={{
                      ...(consumerRTHTableStyles.TableCell as CSSProperties),
                      ...getStyleByIndex(index, colors).style,
                    }}
                    key={
                      rthData.categories[index] + series.name + index.toString()
                    }
                  >
                    {data}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

export default ConsumerRTHTable
